"use client"

type NavigatorWithStandalone = Navigator & { standalone?: boolean }

/** Supported platforms for the Zentjes PWA installation notice. */
export type PwaInstallPlatform = "android" | "ios" | "web"

/** Persistent installation notice state kept in the browser. */
export type PwaInstallState = {
    version: 1
    dismissed: Partial<Record<PwaInstallPlatform, boolean>>
    installed: Partial<Record<PwaInstallPlatform, boolean>>
}

const storageKey = "zentjes:pwa-install:v1"
const fallbackState: PwaInstallState = {
    version: 1,
    dismissed: {},
    installed: {},
}

function canUseWindow() {
    return typeof window !== "undefined"
}

function readInstallState(): PwaInstallState {
    if (!canUseWindow()) return fallbackState

    const rawState = window.localStorage.getItem(storageKey)
    if (!rawState) return fallbackState

    try {
        const parsedState = JSON.parse(rawState) as Partial<PwaInstallState>
        return {
            version: 1,
            dismissed: parsedState.dismissed ?? {},
            installed: parsedState.installed ?? {},
        }
    } catch {
        return fallbackState
    }
}

function writeInstallState(state: PwaInstallState) {
    if (!canUseWindow()) return

    window.localStorage.setItem(storageKey, JSON.stringify(state))
}

/**
 * @returns The current browser platform for PWA installation guidance.
 */
export function getPwaInstallPlatform(): PwaInstallPlatform {
    if (!canUseWindow()) return "web"

    const userAgent = window.navigator.userAgent.toLowerCase()
    const isAndroid = userAgent.includes("android")
    const isIOS = /iphone|ipad|ipod/.test(userAgent) || (userAgent.includes("mac") && window.navigator.maxTouchPoints > 1)

    if (isAndroid) return "android"
    if (isIOS) return "ios"

    return "web"
}

/**
 * @returns Whether the app is currently running from an installed PWA surface.
 */
export function isPwaStandalone() {
    if (!canUseWindow()) return false

    return (
        window.matchMedia("(display-mode: standalone)").matches ||
        window.matchMedia("(display-mode: fullscreen)").matches ||
        window.matchMedia("(display-mode: minimal-ui)").matches ||
        Boolean((window.navigator as NavigatorWithStandalone).standalone)
    )
}

/**
 * @param platform Platform that should no longer receive installation notices.
 */
export function markPwaInstalled(platform: PwaInstallPlatform) {
    if (!canUseWindow()) return

    const state = readInstallState()
    writeInstallState({
        ...state,
        installed: {
            ...state.installed,
            [platform]: true,
        },
    })
}

/**
 * @param platform Platform where the installation notice was dismissed.
 */
export function markPwaInstallNoticeDismissed(platform: PwaInstallPlatform) {
    if (!canUseWindow()) return

    const state = readInstallState()
    writeInstallState({
        ...state,
        dismissed: {
            ...state.dismissed,
            [platform]: true,
        },
    })
}

/**
 * @param platform Platform to check before showing installation guidance.
 * @returns Whether the installation notice should stay hidden.
 */
export function hasHandledPwaInstallNotice(platform: PwaInstallPlatform) {
    if (!canUseWindow()) return true
    if (isPwaStandalone()) return true

    const state = readInstallState()

    return Boolean(state.installed[platform] || state.dismissed[platform])
}
